import { useMemo } from 'react'
import { useSnapshot } from 'valtio'
import { debounce } from 'lodash-es'
import Cookies from 'cookies-js'
import { intl } from 'lang/intl'

import { type Comment, type CommentStatus } from '../models/Comment'
import {
  addComment,
  promoteComment,
  setCommentOrder,
  updateComment,
} from '../api/commentApi'
import {
  getLiveId,
  getPresenterToken,
  getRole,
} from '../../global/blocs/roomInfo'
import { getLiveToken } from '../../global/blocs/useConnectInfo'
import { isErrorWithApiCause } from '../../global/api/legacyApi'
import { showNotice } from '../../Layout/Notices/Notices'

import {
  chatMessagesState,
  getRemoteChatMessage,
  pinMessage,
  reorderQuestions,
  useQuestionIds,
} from './useChat'

type QuestionGroupKey = CommentStatus | 'pinned'

type QuestionGroups = Record<QuestionGroupKey, Comment[]>

const getGroupKey = (question: Comment): QuestionGroupKey =>
  question.status === '' && question.pinned ? 'pinned' : question.status

const useQuestions = () => {
  const ids = useQuestionIds()
  const { messages } = useSnapshot(chatMessagesState)

  return useMemo(() => {
    const groups: QuestionGroups = {
      pinned: [],
      '': [],
      answered: [],
      dismissed: [],
    }
    ids?.forEach((id) => {
      if (id === undefined) return
      const question = messages.get(id)
      if (question) groups[getGroupKey(question)].push(question)
    })

    return { groups }
  }, [ids, messages])
}

const pendingPinRequests = new Map<number, Promise<unknown>>()

const pinQuestion = async (commentId: number, pin: boolean) => {
  pinMessage(commentId, pin)

  const send = () => promoteComment({ comment_id: commentId, promoted_p: pin })
  const previous = pendingPinRequests.get(commentId)
  const request = previous ? previous.catch(() => undefined).then(send) : send()
  pendingPinRequests.set(commentId, request)

  try {
    await request
  } catch {
    if (pendingPinRequests.get(commentId) !== request) return
    pinMessage(commentId, !pin)
    showNotice({
      message: pin
        ? intl.formatMessage({
            defaultMessage: 'Could not pin the question',
            id: 'x8E1Kd',
          })
        : intl.formatMessage({
            defaultMessage: 'Could not unpin the question',
            id: 'lx4wjH',
          }),
      expirationMillis: 5000,
    })
  } finally {
    if (pendingPinRequests.get(commentId) === request) {
      pendingPinRequests.delete(commentId)
    }
  }
}

const setQuestionStatus = async (commentId: number, status: CommentStatus) => {
  const question = chatMessagesState.messages.get(commentId)
  const previousStatus = question?.status
  if (question) question.status = status

  try {
    await updateComment({
      object_id: getLiveId(),
      comment_id: commentId,
      comment_status: status,
    })
    if (question?.pinned && status !== '') void pinQuestion(commentId, false)
  } catch {
    if (question && previousStatus !== undefined) {
      question.status = previousStatus
    }
    showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Could not update the question',
        id: '3TqzJv',
      }),
      expirationMillis: 5000,
    })
  }
}

const persistQuestionOrder = debounce((order: number[]) => {
  setCommentOrder({
    object_id: getLiveId(),
    order: order.join(','),
  }).catch(() => {
    showNotice({
      message: intl.formatMessage({
        defaultMessage: 'Could not set the order of comments',
        id: '13pz9N',
      }),
      expirationMillis: 5000,
    })
  })
}, 1000)

const getOpenQuestionIds = () => {
  const pinned: number[] = []
  const unpinned: number[] = []
  chatMessagesState.ids.question?.forEach((id) => {
    if (id === undefined) return
    const question = chatMessagesState.messages.get(id)
    if (question?.status !== '') return
    if (question.pinned) pinned.push(id)
    else unpinned.push(id)
  })
  return { pinned, unpinned }
}

/**
 * Takes the open questions in their new order — pinned first, then the rest —
 * pins or unpins those that changed list, and appends everything else in its
 * existing order.
 */
const moveQuestions = (pinnedOrder: number[], unpinnedOrder: number[]) => {
  const ids = chatMessagesState.ids.question
  if (!ids) return

  const { messages } = chatMessagesState
  const pinChanges = [
    ...pinnedOrder
      .filter((id) => messages.get(id)?.pinned === false)
      .map((id) => [id, true] as const),
    ...unpinnedOrder
      .filter((id) => messages.get(id)?.pinned === true)
      .map((id) => [id, false] as const),
  ]

  const openOrder = [...pinnedOrder, ...unpinnedOrder]
  const moved = new Set(openOrder)
  const rest = ids.filter(
    (id): id is number => id !== undefined && !moved.has(id)
  )
  const newOrder = [...openOrder, ...rest]

  reorderQuestions(newOrder)
  persistQuestionOrder(newOrder)

  if (pinChanges.length === 0) return
  persistQuestionOrder.flush()
  pinChanges.forEach(([id, pin]) => void pinQuestion(id, pin))
}

/**
 * Pinning puts the question at the bottom of the pinned list; unpinning puts
 * it at the top of the unpinned one.
 */
const toggleQuestionPin = (commentId: number) => {
  const { pinned, unpinned } = getOpenQuestionIds()
  const otherPinned = pinned.filter((id) => id !== commentId)
  const otherUnpinned = unpinned.filter((id) => id !== commentId)

  if (pinned.includes(commentId)) {
    moveQuestions(otherPinned, [commentId, ...otherUnpinned])
  } else {
    moveQuestions([...otherPinned, commentId], otherUnpinned)
  }
}

const askQuestion = async (content: string, name?: string) => {
  const isAudience = getRole() === 'audience'

  await addComment({
    object_id: getLiveId(),
    object_type: 'live',
    object_token: await getLiveToken(),
    comment_type: 'question',
    content,
    uuid: isAudience ? Cookies.get('uuid') : undefined,
    presenter_token: getPresenterToken(),
    name,
  })
}

const getAddQuestionErrorMessage = (error: unknown) => {
  const code = isErrorWithApiCause(error) ? error.cause.code : undefined

  switch (code) {
    case 'permission_denied':
      return intl.formatMessage({
        defaultMessage: 'You are not allowed to add questions',
        id: 'lWQr7a',
      })
    case 'content_is_required':
      return intl.formatMessage({
        defaultMessage: 'The question cannot be empty',
        id: 'xYDa/T',
      })
    default:
      return intl.formatMessage({
        defaultMessage: 'Could not add the question',
        id: 'O5YqMe',
      })
  }
}

const showAddedQuestion = async (commentId: number) => {
  await getRemoteChatMessage(commentId)
  if (getRole() !== 'producer') return

  const { pinned, unpinned } = getOpenQuestionIds()
  moveQuestions(pinned, [
    commentId,
    ...unpinned.filter((id) => id !== commentId),
  ])
}

const addQuestion = async (content: string, name?: string) => {
  let commentId: number
  try {
    const response = await addComment({
      object_id: getLiveId(),
      object_type: 'live',
      object_token: await getLiveToken(),
      comment_type: 'question',
      content,
      name,
      presenter_token: getPresenterToken(),
      anonymous_p: true,
    })
    commentId = response.data.comment_id
  } catch (error) {
    showNotice({
      message: getAddQuestionErrorMessage(error),
      expirationMillis: 5000,
    })
    return false
  }

  showAddedQuestion(commentId).catch((error: unknown) => {
    console.error('Could not load the added question', error)
  })
  return true
}

export {
  type QuestionGroupKey,
  type QuestionGroups,
  useQuestions,
  setQuestionStatus,
  moveQuestions,
  toggleQuestionPin,
  askQuestion,
  addQuestion,
}
